/**
 * 管理后台画布全屏预览。
 *
 * 画布区域直接复用用户端真实渲染栈（NodeRenderer + CanvasEdgeLayer），
 * 节点以只读方式呈现；左侧节点清单用于定位和查看详情，
 * 不再使用简化的"节点示意图"渲染。
 */
import { useState, useEffect, useMemo } from 'react';
import { Loader2, X } from 'lucide-react';
import { platformFetch } from '../../../platform/http';
import { BoardPreviewCanvas, type PreviewProject } from './BoardPreviewCanvas';
import { NodeDetailPanel } from './NodeDetailPanel';
import { nodeTypeLabel, type PreviewNode } from './previewNode';
import type { CanvasBackground, CanvasEdge, CanvasNode } from '../../../apps/board/types';

type CanvasNodeRaw = PreviewNode;

interface CanvasEdgeRaw {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
}

interface ProjectDetail {
  id: string;
  name: string;
  background: string;
  theme: string;
  zoom: number;
  viewportX: number;
  viewportY: number;
  createdAt: string;
  updatedAt: string;
  user?: { email: string; name: string | null };
  nodes: CanvasNodeRaw[];
  edges: CanvasEdgeRaw[];
}

interface CanvasPreviewModalProps {
  projectId: string;
  onClose: () => void;
}

/** 节点清单摘要：优先图号/标题，其次提示词或文本内容 */
function nodeSummary(node: PreviewNode): string {
  const data = node.data || {};
  if (typeof data.refNo === 'number') return `#${data.refNo}`;
  if (typeof data._title === 'string' && data._title) return data._title.slice(0, 28);
  if (typeof data.prompt === 'string' && data.prompt) return data.prompt.slice(0, 28);
  if (typeof data.content === 'string') {
    const text = data.content.replace(/<[^>]+>/g, ' ').trim();
    if (text) return text.slice(0, 28);
  }
  if (typeof data.instruction === 'string' && data.instruction) return data.instruction.slice(0, 28);
  if (typeof data.modelId === 'string' && data.modelId) return data.modelId;
  if (typeof data.label === 'string' && data.label) return data.label;
  return '';
}

export function CanvasPreviewModal({ projectId, onClose }: CanvasPreviewModalProps) {
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<PreviewNode | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await platformFetch(`/admin/canvas/projects/${projectId}`);
        if (!res.ok) throw new Error('加载失败');
        const json = await res.json();
        if (cancelled) return;
        setProject(json.data as ProjectDetail);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : '加载失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const previewProject = useMemo<PreviewProject | null>(() => {
    if (!project) return null;
    return {
      id: project.id,
      name: project.name,
      viewportX: project.viewportX,
      viewportY: project.viewportY,
      zoom: project.zoom,
      background: (project.background === 'grid' || project.background === 'blank'
        ? project.background
        : 'dot') as CanvasBackground,
      theme: project.theme === 'dark' ? 'dark' : 'light',
      nodes: project.nodes as unknown as CanvasNode[],
      edges: project.edges as unknown as CanvasEdge[],
    };
  }, [project]);

  // 节点清单按画布坐标排序，和画布上的位置关系对应
  const sortedNodes = useMemo(() => {
    if (!project) return [];
    return [...project.nodes].sort((a, b) => (a.y - b.y) || (a.x - b.x));
  }, [project]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/95 backdrop-blur-sm">
      {/* 顶部信息条 */}
      <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <h3 className="text-sm font-semibold text-white truncate">
            {project?.name || '画布预览'}
          </h3>
          {project?.user && (
            <span className="text-xs text-slate-400 shrink-0">
              {project.user.email}
              {project.user.name ? ` · ${project.user.name}` : ''}
            </span>
          )}
          {project && (
            <span className="text-xs text-slate-500 shrink-0">
              {project.nodes.length} 节点 / {project.edges.length} 连线
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-700 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* 节点清单 */}
        {project && (
          <div className="hidden w-60 shrink-0 flex-col border-r border-slate-700 bg-slate-900/60 lg:flex">
            <div className="border-b border-slate-700 px-3 py-2 text-xs font-medium text-slate-400">
              节点清单
            </div>
            <div className="flex-1 overflow-y-auto p-1.5">
              {sortedNodes.length === 0 ? (
                <p className="px-2 py-4 text-center text-xs text-slate-500">空画布</p>
              ) : (
                sortedNodes.map((node) => {
                  const active = selectedNode?.id === node.id;
                  return (
                    <button
                      key={node.id}
                      type="button"
                      onClick={() => setSelectedNode(node)}
                      className={`mb-0.5 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${
                        active ? 'bg-slate-700 text-white' : 'text-slate-300 hover:bg-slate-800'
                      }`}
                    >
                      <span className="shrink-0 text-[10px] text-slate-500">{nodeTypeLabel(node.type)}</span>
                      <span className="truncate">{nodeSummary(node) || '(无内容)'}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* 画布区 */}
        <div className="relative flex-1 overflow-hidden">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : error || !previewProject ? (
            <div className="flex h-full items-center justify-center text-slate-400">{error || '加载失败'}</div>
          ) : (
            <>
              <BoardPreviewCanvas project={previewProject} selectedNodeId={selectedNode?.id ?? null} />
              <div className="pointer-events-none absolute right-4 top-4 rounded-lg border border-slate-700 bg-slate-800/80 px-3 py-1.5 text-[11px] text-slate-400 backdrop-blur-sm">
                真实节点渲染 · 拖拽平移 · 滚轮缩放
              </div>
            </>
          )}
        </div>

        {/* 节点详情 */}
        {selectedNode && (
          <div className="w-80 shrink-0 border-l border-slate-700 bg-white">
            <NodeDetailPanel node={selectedNode} onClose={() => setSelectedNode(null)} />
          </div>
        )}
      </div>
    </div>
  );
}
